import type { PlayerIndex } from "@playpal/core";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { play } from "../lib/sound";

export const PLAYER_STYLE = [
	{
		name: "Red",
		solid: "bg-rose-500 text-white",
		soft: "bg-rose-100 text-rose-900 dark:bg-rose-500/20 dark:text-rose-100",
		ring: "ring-rose-400",
		dot: "bg-rose-500",
		stroke: "stroke-rose-500",
		fill: "fill-rose-500",
	},
	{
		name: "Blue",
		solid: "bg-sky-500 text-white",
		soft: "bg-sky-100 text-sky-900 dark:bg-sky-500/20 dark:text-sky-100",
		ring: "ring-sky-400",
		dot: "bg-sky-500",
		stroke: "stroke-sky-500",
		fill: "fill-sky-500",
	},
] as const;

export function playerStyle(seat: PlayerIndex) {
	return PLAYER_STYLE[seat];
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	tone?: "primary" | "secondary" | "ghost";
	size?: "lg" | "md" | "sm";
	children: ReactNode;
};

const TONES = {
	primary:
		"bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 hover:bg-indigo-500 active:translate-y-0.5",
	secondary:
		"bg-white/80 text-indigo-900 shadow-md hover:bg-white dark:bg-white/10 dark:text-indigo-50 dark:hover:bg-white/20 active:translate-y-0.5",
	ghost:
		"bg-transparent text-indigo-900/70 hover:bg-white/40 dark:text-indigo-100/80 dark:hover:bg-white/10",
} as const;

const SIZES = {
	lg: "px-8 py-4 text-2xl",
	md: "px-5 py-2.5 text-lg",
	// Still a 40px tall target — small enough for the side actions on a card,
	// never used for anything a child is actually here to press.
	sm: "px-4 py-2 text-base",
} as const;

/** Big, round and obvious — and every press makes a noise. */
export function Button({
	tone = "primary",
	size = "md",
	className = "",
	onClick,
	children,
	...rest
}: ButtonProps) {
	return (
		<button
			type="button"
			className={`rounded-2xl font-bold transition disabled:cursor-not-allowed disabled:opacity-40 ${TONES[tone]} ${SIZES[size]} ${className}`}
			onClick={(event) => {
				play("tap");
				onClick?.(event);
			}}
			{...rest}
		>
			{children}
		</button>
	);
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
	return (
		<div
			className={`rounded-3xl bg-white/70 p-6 shadow-xl backdrop-blur-sm dark:bg-white/10 ${className}`}
		>
			{children}
		</div>
	);
}

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
	return (
		// The deep bottom padding is thumb room at the end of a scrolling phone
		// page. On a short landscape tablet nothing is meant to scroll, so it is
		// 64px of the board's height being given away.
		<div
			className={`mx-auto w-full max-w-3xl px-4 pb-16 pt-6 sm:pt-10 deck:pb-4 deck:pt-4 ${className}`}
		>
			{children}
		</div>
	);
}
